/**
 * TRAIN A PERK (04 §2.3): Tag Recovery's reward. Recovered tags make one of the fallen ranger's
 * perks teachable to one living ranger; this picks who. Perk first (in the HQ dialog), then the
 * man, then a confirmation - the perk is used up - and `GameApi.trainPerk` does the rest.
 */

import type { Operation, PerkId, RangerRecord } from '../../../meta/types';
import type { Ctx } from '../context';
import { box, button, el, errorMessage } from '../dom';
import { serviceName } from '../text';
import { confirm, dialog, notice } from '../widgets/confirm';

/** With the company and able to learn: not dead, not lost, not retired, not a prisoner, not walking out. Pure. */
export function canBeTaught(r: RangerRecord, perk: PerkId): boolean {
  const present = r.status === 'ready' || r.status === 'wounded' || r.status === 'critical';
  return present && !r.perks.includes(perk);
}

/** The perks on offer, without repeats. Pure. */
export function trainablePerks(op: Operation): PerkId[] {
  return [...new Set(op.trainablePerks ?? [])];
}

/** Pick the ranger for `perk`; `onResolved` runs after the player has read the result. */
export function openTrainDialog(ctx: Ctx, op: Operation, perk: PerkId, onResolved: () => void): void {
  const about = ctx.describe(perk);
  const d = dialog(`TRAIN: ${about.name}`, () => ctx.closeModal(), 'menu-dialog-wide');
  if (about.text !== '') d.body.append(box('menu-prose', about.text));
  d.body.append(
    box('menu-prose menu-dim', 'ONE RANGER, ONCE: THE PERK IS USED UP WHEN IT IS TAUGHT. RANGERS WHO ALREADY HAVE IT,'
      + ' AND THOSE NOT WITH THE COMPANY, ARE NOT LISTED.'),
  );

  const teach = (r: RangerRecord): void =>
    confirm(ctx, {
      title: 'TRAIN A PERK',
      lines: [`TEACH ${about.name} TO ${serviceName(r)}?`, 'THE PERK CAN BE TAUGHT ONCE. THIS CANNOT BE UNDONE.'],
      confirmLabel: 'TEACH HIM',
      onConfirm: () => {
        try {
          ctx.api.trainPerk(r.id, perk);
        } catch (err) {
          notice(ctx, 'NOT TRAINED', [errorMessage(err)]);
          return;
        }
        notice(ctx, 'PERK TRAINED', [`${serviceName(r)} NOW HAS ${about.name}.`], onResolved);
      },
    });

  const pupils = op.company.filter((r) => canBeTaught(r, perk));
  const buttons = pupils.map((r) => {
    const b = button(serviceName(r), () => teach(r), 'menu-btn-small');
    b.setAttribute('aria-label', `TEACH ${about.name} TO ${serviceName(r)}`);
    return b;
  });
  if (buttons.length === 0) d.body.append(el('div', 'menu-warn', '! NOBODY WITH THE COMPANY CAN LEARN THIS NOW.'));
  else d.body.append(box('menu-row menu-row-wrap', ...buttons));

  const close = button('NOT NOW', () => ctx.closeModal());
  d.actions.append(close);
  ctx.openModal({ ...d.modal, initialFocus: () => buttons[0] ?? close });
}
